import React, { Component, ReactNode } from 'react';
import { Product, ReferencePrice, SkMaxPrice } from '~/Api/User/ProductGroup/SkMaxPrice';
import { formatCountriesError } from '~/UI';
import styles from './styles.less';
import parseHumanFloat from '~/utils/Number/parseHumanFloat';
import { calculatePrice } from '../calculatePrice';
import { ProductValues } from '../ProductsTable/ProductsTable';
import memoizeOne from 'memoize-one';
import { Typography } from '~/libs/ui/atoms/Typography';
import { CountrySourceWithTooltip } from '~/libs/ui/molecules/CountrySourceWithTooltip';

interface Props {
	skMaxPrice: SkMaxPrice;
	productValues: { [id: string]: ProductValues };
	referencePackageSize: number | null;
	formatPercentage(percentage: number): string;
}

export default class Calculation extends Component<Props> {
	private renderSamePackageSizeMessage() {
		const {
			skMaxPrice: { finalCalculation, products },
		} = this.props;
		const diffPackageSizeCount =
			finalCalculation.threeLowestPricesAvg?.fromProducts.reduce(
				(count, product) => count + (product.samePackageSize ? 0 : 1),
				0
			) ?? 0;
		const samePackageSizeCount =
			(finalCalculation.threeLowestPricesAvg?.fromProducts.length ?? 0) - diffPackageSizeCount;

		switch (true) {
			case diffPackageSizeCount === 0:
				return <>Average of 3 lowest recalculated ex-factory prices</>;
			case diffPackageSizeCount === 1 && samePackageSizeCount === 0:
				return <>1st lowest price was recalculated from products with different package sizes.</>;
			case diffPackageSizeCount === 1 && samePackageSizeCount === 1:
				return <>2nd lowest price was recalculated from products with different package sizes.</>;
			case diffPackageSizeCount === 1 && samePackageSizeCount === 2:
				return <>3rd lowest price was recalculated from products with different package sizes.</>;
			case diffPackageSizeCount === 2 && samePackageSizeCount === 0:
				return <>1st and 2nd lowest prices were recalculated from products with different package sizes.</>;
			case diffPackageSizeCount === 2 && samePackageSizeCount === 1:
				return <>2nd and 3rd lowest prices were recalculated from products with different package sizes.</>;
			case diffPackageSizeCount === 3:
				return <>All lowest prices were recalculated from products with different package sizes.</>;
			default:
				return null;
		}
	}

	renderEmptyResultReason(): ReactNode {
		const {
			skMaxPrice: { finalCalculation },
		} = this.props;
		const countryError = finalCalculation.requiresOnlyOneProductPerCountry();

		if (countryError !== null) {
			return (
				<strong className={styles.rowError}>
					Calculation requires at most one product for each country
					{formatCountriesError(countryError.duplicateCountries)}
				</strong>
			);
		}
		if (finalCalculation.requiresProductSettings()) {
			return <strong className={styles.rowError}>Some products settings are missing</strong>;
		}
		if (finalCalculation.requiresAtLeastOneReferenceCountryProduct()) {
			return <strong className={styles.rowError}>Calculation requires at least one reference country product</strong>;
		}
		if (finalCalculation.requiresReferenceSettings()) {
			return <strong className={styles.rowError}>Calculation requires reference settings</strong>;
		}
		if (finalCalculation.illegalPriceTypeSelected()) {
			return <strong className={styles.rowError}>Some products have illegal price selected</strong>;
		}
		return null;
	}

	resolveReferencePrice = memoizeOne(
		(type: string | null, skMaxPriceProduct: Product | undefined): ReferencePrice | null => {
			if (type === null) {
				return null;
			}
			return (
				skMaxPriceProduct?.referencePrices.find((referencePrice) => referencePrice.calculatedPrice.type === type) ??
				null
			);
		}
	);

	renderCalculatedPrice(id: string) {
		const { skMaxPrice, productValues, referencePackageSize } = this.props;

		const packageSize = productValues[id].packageSize;

		if (
			packageSize.isValid === false ||
			(packageSize.isValid === null && packageSize.value !== '') ||
			referencePackageSize === null
		) {
			return null;
		}

		const parsedPackageSize = packageSize.value !== '' ? parseHumanFloat(packageSize.value) : referencePackageSize;

		if (parsedPackageSize === null) {
			return null;
		}

		const referencePrice = skMaxPrice.products
			.find((x) => x.product.id === id)
			?.referencePrices.find((x) => x.calculatedPrice.selected);

		if (!referencePrice) return;
		return calculatePrice(
			referencePrice.calculatedPrice.targetCurrencyPrice,
			parsedPackageSize,
			referencePackageSize
		).roundedHumanFormat();
	}

	renderCalculatedERCPrice(id: string) {
		const { skMaxPrice, productValues, referencePackageSize } = this.props;

		const packageSize = productValues[id].packageSize;
		const referencePrice = this.resolveReferencePrice(
			productValues[id].referencePrice.value?.value ?? null,
			skMaxPrice.products.find((x) => x.product.id === id)
		);
		if (
			packageSize.isValid === false ||
			(packageSize.isValid === null && packageSize.value !== '') ||
			referencePackageSize === null
		) {
			return null;
		}

		const parsedPackageSize = packageSize.value !== '' ? parseHumanFloat(packageSize.value) : referencePackageSize;

		if (parsedPackageSize === null) {
			return null;
		}

		if (!referencePrice) return;
		const calcPrice = calculatePrice(
			referencePrice.calculatedPrice.targetCurrencyPrice,
			parsedPackageSize,
			referencePackageSize
		);
		const fxComparisonReferencePrice = skMaxPrice.products.find((x) => x.product.id === id)?.fxComparisonReferencePrice
			?.calculatedPrice.targetCurrencyPrice;

		if (!fxComparisonReferencePrice) return;
		const calcFxComparisonReferencePrice = calculatePrice(
			fxComparisonReferencePrice,
			parsedPackageSize,
			referencePackageSize
		);

		const fxComparisonResolution = skMaxPrice.products.find((x) => x.product.id === id)?.fxComparisonResolution;

		if (fxComparisonResolution === 'non-applicable') return calcPrice.roundedHumanFormat();

		return calcPrice.value >= calcFxComparisonReferencePrice.value
			? calcPrice.roundedHumanFormat()
			: calcFxComparisonReferencePrice.roundedHumanFormat();
	}

	render() {
		const {
			skMaxPrice: { finalCalculation, products },
			formatPercentage,
		} = this.props;

		const isErcEqual =
			!!finalCalculation.threeLowestPricesAvg &&
			finalCalculation.threeLowestPricesAvg.ercPrice.value === finalCalculation.threeLowestPricesAvg.price.value;

		const isErcHigher =
			!!finalCalculation.threeLowestPricesAvg &&
			finalCalculation.threeLowestPricesAvg.ercPrice.value > finalCalculation.threeLowestPricesAvg.price.value;

		return (
			<div className={styles.container}>
				{finalCalculation.emptyResultReason.length > 0 ? (
					<div className='mb-4'>
						<p>{this.renderEmptyResultReason()}</p>
					</div>
				) : null}
				<div className={styles.columns}>
					<div className={styles.leftColumn}>
						<div className=''>
							<Typography level={5} color='black'>
								Lowest recalculated ex-factory prices:
							</Typography>
							<div className='divide-y divide-gray-200'>
								{finalCalculation.threeLowestPricesAvg?.fromProducts.map((product, i) => (
									<div
										className='w-full flex flex-nowrap items-center justify-between py-2'
										data-product-id={product.id}
										data-testid={`lowest-exfactory-price-${i}`}
										key={product.id}
									>
										<div className='w-0 max-w-32_ grow shrink flex flex-nowrap items-center justify-start'>
											<span className='grow-0 shrink-0 w-6 text-gray-500 text-left'>{i + 1}.</span>
											<CountrySourceWithTooltip
												countryAlpha={product.dataSource.country.alpha2}
												countryName={product.dataSource.country.name}
												dataSourceShortName={product.dataSource.shortName}
												dataSourceDescription={product.dataSource.description}
											/>
										</div>
										<div className='block pl-2 grow-0 shrink-0 text-[14px] leading-tight tracking-wider'>
											{this.renderCalculatedPrice(products.filter((x) => x.product.id === product.id)[0].product.id)}
										</div>
									</div>
								))}
							</div>
						</div>
						<div
							className={`mt-0.5 pt-1 ${
								finalCalculation.emptyResultReason.length <= 0 && 'pl-6 border-t border-gray-200'
							}`}
						>
							<div className='flex items-center justify-between'>
								<div className='pr-2'>
									<strong className='text-[14px] font-normal leading-tight tracking-wider'>Current price:</strong>
								</div>
								<div className='block text-[14px] leading-tight tracking-wider'>
									<strong>{finalCalculation.referencedProduct?.currentExFactoryPrice?.roundedHumanFormat()}</strong>
								</div>
							</div>
							<div className='mt-2.5'>
								<div className='flex items-start justify-between'>
									<div className='pr-2'>
										<strong className='block text-[14px] font-normal leading-tight tracking-wider'>
											Average prices:
										</strong>
									</div>
									<div className='relative block text-[14px] leading-tight tracking-wider'>
										<strong>{finalCalculation.threeLowestPricesAvg?.price.roundedHumanFormat()}</strong>
										{!isErcEqual && !isErcHigher && finalCalculation.threeLowestPricesAvg && (
											<div className={styles.rowPriceArrowTop}>
												<svg
													xmlns='http://www.w3.org/2000/svg'
													viewBox='0 0 20 20'
													fill='currentColor'
													className='w-5 h-5'
												>
													<path
														fillRule='evenodd'
														d='M10 18a.75.75 0 01-.75-.75V4.66L7.3 6.76a.75.75 0 11-1.1-1.02l3.25-3.5a.75.75 0 011.1 0l3.25 3.5a.75.75 0 01-1.1 1.02l-1.95-2.1v12.59A.75.75 0 0110 18z'
														clipRule='evenodd'
													/>
												</svg>
											</div>
										)}
										{!isErcEqual && isErcHigher && finalCalculation.threeLowestPricesAvg && (
											<div className={styles.rowPriceArrowBottom}>
												<svg
													xmlns='http://www.w3.org/2000/svg'
													viewBox='0 0 20 20'
													fill='currentColor'
													className='w-5 h-5'
												>
													<path
														fillRule='evenodd'
														d='M10 2a.75.75 0 01.75.75v12.59l1.95-2.1a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 111.1-1.02l1.95 2.1V2.75A.75.75 0 0110 2z'
														clipRule='evenodd'
													/>
												</svg>
											</div>
										)}
									</div>
								</div>
								<span className='block mt-0.5 min-h-8 text-[11px] leading-tight text-gray-400 tracking-wide'>
									{this.renderSamePackageSizeMessage()}
								</span>
							</div>
							<div className='flex items-start justify-between mt-2.5'>
								<div className='pr-2'>
									<strong className='block text-[14px] font-normal leading-tight tracking-wider'>Price drop:</strong>
								</div>
								<div className='block text-[14px] leading-tight tracking-wider'>
									<strong>
										{finalCalculation.priceDropPercents ? formatPercentage(finalCalculation.priceDropPercents) : null}
									</strong>
								</div>
							</div>
						</div>
					</div>
					<div className={styles.secondColumn}>
						<div className=''>
							<Typography level={5} color='black'>
								ERC Lowest prices:
							</Typography>
							<div className='divide-y divide-gray-200'>
								{finalCalculation.threeLowestPricesAvg?.ercFromProducts.map((product, i) => (
									<div
										className='w-full flex items-center justify-between py-2'
										data-product-id={product.id}
										data-testid={`lowest-erc-price-${i}`}
										key={product.id}
									>
										<div className='w-0 max-w-32_ grow shrink flex flex-nowrap items-center justify-start'>
											<span className='grow-0 shrink-0 w-6 text-gray-500 text-left'>{i + 1}.</span>
											<CountrySourceWithTooltip
												countryAlpha={product.dataSource.country.alpha2}
												countryName={product.dataSource.country.name}
												dataSourceShortName={product.dataSource.shortName}
												dataSourceDescription={product.dataSource.description}
											/>
										</div>
										<div className='block pl-2 text-[14px] leading-tight tracking-wider'>
											{this.renderCalculatedERCPrice(products.filter((x) => x.product.id === product.id)[0].product.id)}
										</div>
									</div>
								))}
							</div>
						</div>
						<div
							className={`mt-0.5 pt-1 ${
								finalCalculation.emptyResultReason.length <= 0 && 'pl-6 border-t border-gray-200'
							}`}
						>
							<div className='flex items-center justify-between'>
								<div className='pr-2'>
									<strong className='text-[14px] font-normal leading-tight tracking-wider'>Current price:</strong>
								</div>
								<div className='block text-[14px] leading-tight tracking-wider'>
									<strong>{finalCalculation.referencedProduct?.currentExFactoryPrice?.roundedHumanFormat()}</strong>
								</div>
							</div>
							<div className='mt-2.5'>
								<div className='flex items-start justify-between'>
									<div className='pr-2'>
										<strong className='block text-[14px] font-normal leading-tight tracking-wider'>ERC price:</strong>
									</div>
									<div className='relative block text-[14px] leading-tight tracking-wider'>
										<strong>{finalCalculation.threeLowestPricesAvg?.ercPrice.roundedHumanFormat()}</strong>
										{!isErcEqual && isErcHigher && finalCalculation.threeLowestPricesAvg && (
											<div className={styles.rowPriceArrowTop}>
												<svg
													xmlns='http://www.w3.org/2000/svg'
													viewBox='0 0 20 20'
													fill='currentColor'
													className='w-5 h-5'
												>
													<path
														fillRule='evenodd'
														d='M10 18a.75.75 0 01-.75-.75V4.66L7.3 6.76a.75.75 0 11-1.1-1.02l3.25-3.5a.75.75 0 011.1 0l3.25 3.5a.75.75 0 01-1.1 1.02l-1.95-2.1v12.59A.75.75 0 0110 18z'
														clipRule='evenodd'
													/>
												</svg>
											</div>
										)}
										{!isErcEqual && !isErcHigher && finalCalculation.threeLowestPricesAvg && (
											<div className={styles.rowPriceArrowBottom}>
												<svg
													xmlns='http://www.w3.org/2000/svg'
													viewBox='0 0 20 20'
													fill='currentColor'
													className='w-5 h-5'
												>
													<path
														fillRule='evenodd'
														d='M10 2a.75.75 0 01.75.75v12.59l1.95-2.1a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 111.1-1.02l1.95 2.1V2.75A.75.75 0 0110 2z'
														clipRule='evenodd'
													/>
												</svg>
											</div>
										)}
									</div>
								</div>
								<span className='block mt-0.5 min-h-8 text-[11px] leading-tight text-gray-400 tracking-wide'>
									{`Price on the selected date was ${
										isErcHigher ? 'higher' : 'lower'
									} than today, due to the exchange rates.`}
								</span>
							</div>
							<div className='flex items-start justify-between mt-2.5'>
								<div className='pr-2'>
									<strong className='block text-[14px] font-normal leading-tight tracking-wider'>
										ERC price drop:
									</strong>
								</div>
								<div className='block text-[14px] leading-tight tracking-wider'>
									<strong>
										{finalCalculation.ercPriceDropPercents
											? formatPercentage(finalCalculation.ercPriceDropPercents)
											: null}
									</strong>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
}
